import { useEffect, useMemo, useState } from 'react';
import { Clock } from '@phosphor-icons/react';
import { useSettings } from '@/context/SettingsContext';

/**
 * Live sale countdown timer. Reads settings.sale_timer.
 * Auto-hides when disabled, not-yet-started, or expired.
 * Updates once per second locally — no polling.
 */
export default function SaleTimer() {
  const { settings } = useSettings();
  const timer = settings?.sale_timer;
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!timer?.enabled || !timer?.end_iso) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [timer?.enabled, timer?.end_iso]);

  const { active, remainingMs } = useMemo(() => {
    if (!timer?.enabled || !timer?.end_iso) return { active: false, remainingMs: 0 };
    const end = new Date(timer.end_iso).getTime();
    if (isNaN(end)) return { active: false, remainingMs: 0 };
    const start = timer.start_iso ? new Date(timer.start_iso).getTime() : 0;
    if (start && now < start) return { active: false, remainingMs: 0 };
    const remaining = end - now;
    return { active: remaining > 0, remainingMs: remaining };
  }, [timer, now]);

  if (!active) return null;

  const total = Math.max(0, remainingMs);
  const days = Math.floor(total / (1000 * 60 * 60 * 24));
  const hours = Math.floor((total / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((total / (1000 * 60)) % 60);
  const seconds = Math.floor((total / 1000) % 60);
  const pad = (n) => String(n).padStart(2, '0');

  return (
    <section
      data-testid="sale-timer"
      className="rounded-2xl bg-gradient-to-r from-terracotta-500 to-terracotta-600 text-white px-4 py-3 sm:px-6 sm:py-4 flex flex-wrap items-center gap-3 sm:gap-5 shadow-md"
    >
      <div className="flex items-center gap-2">
        <Clock size={22} weight="fill" />
        <span className="font-heading text-sm sm:text-base">{timer.title || 'Sale ends in'}</span>
      </div>
      <div className="flex items-center gap-2 sm:gap-3 ml-auto" data-testid="sale-timer-countdown">
        <TimeCell value={days} label="Days" testid="timer-days" />
        <TimeCell value={pad(hours)} label="Hours" testid="timer-hours" />
        <TimeCell value={pad(minutes)} label="Min" testid="timer-minutes" />
        <TimeCell value={pad(seconds)} label="Sec" testid="timer-seconds" />
      </div>
    </section>
  );
}

function TimeCell({ value, label, testid }) {
  return (
    <div className="flex flex-col items-center min-w-[44px] sm:min-w-[54px] bg-white/15 rounded-lg px-2 py-1 backdrop-blur-sm">
      <span data-testid={testid} className="font-heading text-lg sm:text-2xl leading-none tabular-nums">{value}</span>
      <span className="text-[10px] sm:text-xs uppercase tracking-wider opacity-90">{label}</span>
    </div>
  );
}
